import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy } from './base_strategy';

/**
 * Upper bound for a strategy's REST bootstrap (precision, orderbook seed, ...).
 * Exchange metadata goes through the external proxy without its own timeout;
 * a stalled request must not keep the socket, orderbook and trades dark.
 */
export const STRATEGY_BOOTSTRAP_TIMEOUT_MS = 8000;

/**
 * Runs `instance.fetch()` but returns the instance after at most `timeoutMs`,
 * even if the bootstrap is still pending or failed. A late bootstrap keeps
 * running and still delivers its results through the strategy callbacks.
 */
export const bootstrapStrategy = async <T extends BaseStrategy>(
  instance: T,
  timeoutMs = STRATEGY_BOOTSTRAP_TIMEOUT_MS,
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  await Promise.race([
    instance.fetch().catch(error => {
      console.log('Cex strategy bootstrap error: ', error);
    }),
    new Promise<void>(resolve => {
      timer = setTimeout(resolve, timeoutMs);
    }),
  ]);
  clearTimeout(timer);

  return instance;
};

/** Converts a venue tick size (e.g. "0.00001") into decimal places, or undefined if invalid. */
export const tickSizeToPrecision = (tickSize: unknown): number | undefined => {
  const tick = Number(tickSize);
  if (!Number.isFinite(tick) || tick <= 0) return undefined;

  const normalized = normalizeNumber(tick);
  if (Number(normalized) <= 0) return undefined;

  return normalized.split('.')[1]?.length ?? 0;
};
